const finiteNumberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null
  }

  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

const validPhenologyDate = (value) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null
  }
  const parsed = new Date(`${value}T00:00:00Z`)
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
    ? value
    : null
}

// The stage describes the last historical climate date, not the report generation date.
export const getPhenologyTemporalReference = (analysis, now = new Date()) => {
  const policy = analysis?.periodPolicy || {}
  const observedDate = validPhenologyDate(analysis?.climate?.period?.dateTo)
  const referenceDate = observedDate || validPhenologyDate(policy.analysisEndDate)
  const closed =
    policy.closed === true ||
    policy.cappedAutomatically === true ||
    Boolean(validPhenologyDate(analysis?.profile?.harvestDate))
      ? true
      : policy.closed === false
      ? false
      : null
  const clock = now instanceof Date ? now : new Date(now)
  let today = null
  if (Number.isFinite(clock.getTime())) {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: 'America/Argentina/Buenos_Aires',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(clock)
    const part = (type) => parts.find((item) => item.type === type)?.value
    today = `${part('year')}-${part('month')}-${part('day')}`
  }
  return {
    referenceDate,
    closed,
    // Missing policy/data dates and stale open snapshots must not claim "current".
    isCurrent: closed === false && Boolean(observedDate) && observedDate === today,
  }
}

const TERMINAL_NARRATIVE_PUNCTUATION = /[.!?\u2026]["'\u2019\u201d)\]]*$/
const STRUCTURED_NARRATIVE_LABELS = ['Incertidumbre:', 'Uncertainty:']

const isCompleteNarrativeSentence = (value) => {
  const text = String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
  return Boolean(text && TERMINAL_NARRATIVE_PUNCTUATION.test(text))
}

export const isCompleteCopilotAiText = (value) => {
  if (typeof value !== 'string') {
    return false
  }

  const paragraphs = value
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean)
  if (!paragraphs.length || !isCompleteNarrativeSentence(paragraphs[0])) {
    return false
  }

  return paragraphs.slice(1).every((paragraph) => {
    const label = STRUCTURED_NARRATIVE_LABELS.find((item) => paragraph.startsWith(item))
    return !label || isCompleteNarrativeSentence(paragraph.slice(label.length))
  })
}

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/
const MONTH_DAY_PATTERN = /^(\d{2})-(\d{2})$/
const ECONOMIC_REPORT_FINDING_IDS = new Set([
  'economic-report-missing',
  'economic-summary',
  'yield-coverage',
  'data-quality-warnings',
])
const AMBIENT_PRODUCTIVITY_CODES_BY_CLASS_COUNT = {
  2: ['BP', 'AP'],
  3: ['BP', 'PP', 'AP'],
  4: ['MBP', 'BP', 'AP', 'MAP'],
  5: ['MBP', 'BP', 'PP', 'AP', 'MAP'],
  6: ['MBP', 'BP', 'PPB', 'PPA', 'AP', 'MAP'],
  7: ['MBP', 'BP', 'PPB', 'PP', 'PPA', 'AP', 'MAP'],
}
const AMBIENT_PRODUCTIVITY_NAMES = {
  es: {
    MAP: 'Muy alta productividad',
    AP: 'Alta productividad',
    PPA: 'Productividad promedio alta',
    PP: 'Productividad promedio',
    PPB: 'Productividad promedio baja',
    BP: 'Baja productividad',
    MBP: 'Muy baja productividad',
  },
  en: {
    MAP: 'Very high productivity',
    AP: 'High productivity',
    PPA: 'High average productivity',
    PP: 'Average productivity',
    PPB: 'Low average productivity',
    BP: 'Low productivity',
    MBP: 'Very low productivity',
  },
}
const AMBIENT_PRODUCTIVITY_ORDER = ['MAP', 'AP', 'PPA', 'PP', 'PPB', 'BP', 'MBP']
const ANALYSIS_PERSISTENCE_STATUSES = new Set([
  'saved',
  'failed',
  'not_configured',
  'not_requested',
  'legacy_unknown',
])
const PRODUCTIVITY_ORDERED_FINDING_IDS = new Set([
  'soil-historical-ambient-cross',
  'historical-ambient-soil-boundaries',
])
const AMBIENT_PRODUCTIVITY_REFERENCE_PATTERN = /\b(?:Ambiente|Zone)\s+(MAP|PPA|PPB|AP|PP|BP|MBP)\b/i
const CUMULATIVE_PRECIPITATION_EVIDENCE_LABELS = new Set([
  'Precipitación acumulada',
  'Precipitaciones acumuladas',
  'Accumulated precipitation',
])
const SOURCE_FRESHNESS_STATUSES = new Set(['current', 'updates_available', 'unknown'])

export const getCopilotCampaignIdentity = (campaign, fallback = '') => {
  const value =
    campaign?.campaignKey ||
    campaign?.sourceKey ||
    campaign?.reportId ||
    campaign?.layerId ||
    fallback

  return String(value || '').trim()
}

const formatCampaignSourceDate = (value, locale) => {
  if (!value) {
    return ''
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }

  return new Intl.DateTimeFormat(locale || 'es', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date)
}

const shortCampaignSourceId = (campaign) => {
  const value = String(
    campaign?.reportId || campaign?.layerId || campaign?.sourceKey || ''
  ).replace(/^[^:]+:/, '')
  return value ? value.slice(-8) : ''
}

export const getCopilotCampaignSourceLabel = (campaign, locale = 'es') => {
  const reportName = String(campaign?.reportName || '').trim()
  const date = formatCampaignSourceDate(campaign?.createdAt, locale)
  const shortId = shortCampaignSourceId(campaign)

  if (reportName) {
    return reportName
  }
  if (date) {
    return date
  }
  return shortId
}

export const buildCopilotCampaignLabels = (
  campaigns,
  {locale = 'es', formatCrop = (crop) => String(crop || '').trim()} = {}
) => {
  const records = (Array.isArray(campaigns) ? campaigns : []).map((campaign, index) => {
    const identity = getCopilotCampaignIdentity(campaign, `campaign-${index + 1}`)
    const crop = campaign?.crop ? formatCrop(campaign.crop) : ''
    const base = [campaign?.campaign, crop].filter(Boolean).join(' - ') || '-'
    return {
      campaign,
      identity,
      base,
      source: getCopilotCampaignSourceLabel(campaign, locale),
      date: formatCampaignSourceDate(campaign?.createdAt, locale),
      shortId: shortCampaignSourceId(campaign),
      index,
    }
  })
  const baseCounts = records.reduce((counts, record) => {
    counts.set(record.base, (counts.get(record.base) || 0) + 1)
    return counts
  }, new Map())
  const preliminary = records.map((record) => ({
    ...record,
    label:
      baseCounts.get(record.base) > 1
        ? `${record.base} · ${record.source || record.index + 1}`
        : record.base,
  }))
  const labelCounts = preliminary.reduce((counts, record) => {
    counts.set(record.label, (counts.get(record.label) || 0) + 1)
    return counts
  }, new Map())

  return preliminary.reduce((labels, record) => {
    const qualifier =
      [record.date, record.shortId].filter(Boolean).join(' · ') || String(record.index + 1)
    labels[record.identity] =
      labelCounts.get(record.label) > 1 ? `${record.label} · ${qualifier}` : record.label
    return labels
  }, {})
}

const normalizeSourceFreshness = (value) => {
  const freshness = value && typeof value === 'object' ? value : {}
  const stringList = (items) =>
    Array.isArray(items) ? items.filter((item) => typeof item === 'string' && item.trim()) : []

  return {
    status: SOURCE_FRESHNESS_STATUSES.has(freshness.status) ? freshness.status : 'unknown',
    checkedAt: typeof freshness.checkedAt === 'string' ? freshness.checkedAt : '',
    changes: stringList(freshness.changes),
    checkedSources: stringList(freshness.checkedSources),
    unverifiedSources: stringList(freshness.unverifiedSources),
  }
}

const normalizeAnalysisPersistence = (value) => {
  const persistence = value && typeof value === 'object' ? value : {}
  const status = ANALYSIS_PERSISTENCE_STATUSES.has(persistence.status)
    ? persistence.status
    : 'legacy_unknown'
  const finiteNumber = (candidate) => {
    const number = Number(candidate)
    return Number.isFinite(number) && number >= 0 ? number : 0
  }

  return {
    status,
    analysisRunId: typeof persistence.analysisRunId === 'string' ? persistence.analysisRunId : '',
    savedAt: typeof persistence.savedAt === 'string' ? persistence.savedAt : '',
    attemptedAt: typeof persistence.attemptedAt === 'string' ? persistence.attemptedAt : '',
    attempts: finiteNumber(persistence.attempts),
    code: typeof persistence.code === 'string' ? persistence.code : '',
    retryable: persistence.retryable === true,
    schemaVersion: finiteNumber(persistence.schemaVersion),
    snapshotBytes: finiteNumber(persistence.snapshotBytes),
    artifactCount: finiteNumber(persistence.artifactCount),
    artifactBytes: finiteNumber(persistence.artifactBytes),
  }
}

const normalizeEvidenceLabels = (findings, language) => {
  const cumulativePrecipitationLabel = String(language || '')
    .toLowerCase()
    .startsWith('en')
    ? 'Accumulated precipitation'
    : 'Precipitaciones acumuladas'

  return findings.map((finding) => ({
    ...finding,
    evidence: Array.isArray(finding?.evidence)
      ? finding.evidence.map((item) => ({
          ...item,
          label: CUMULATIVE_PRECIPITATION_EVIDENCE_LABELS.has(item?.label)
            ? cumulativePrecipitationLabel
            : item?.label,
        }))
      : finding?.evidence,
  }))
}

const getAmbientProductivityPriorityFromText = (value) => {
  const match = String(value || '').match(AMBIENT_PRODUCTIVITY_REFERENCE_PATTERN)
  return match ? AMBIENT_PRODUCTIVITY_ORDER.indexOf(match[1].toUpperCase()) : null
}

const sortAmbientEvidence = (evidence) => {
  const ambientEvidence = evidence
    .filter((item) => getAmbientProductivityPriorityFromText(item?.label) !== null)
    .sort(
      (left, right) =>
        getAmbientProductivityPriorityFromText(left?.label) -
        getAmbientProductivityPriorityFromText(right?.label)
    )
  let ambientIndex = 0

  return evidence.map((item) =>
    getAmbientProductivityPriorityFromText(item?.label) === null
      ? item
      : ambientEvidence[ambientIndex++]
  )
}

export const getCopilotAmbientProductivity = (value, ambientValues = [], language = 'es') => {
  const names =
    AMBIENT_PRODUCTIVITY_NAMES[
      String(language || '')
        .toLowerCase()
        .startsWith('en')
        ? 'en'
        : 'es'
    ]
  const rawValue = String(value ?? '').trim()
  const existingCode = rawValue.toUpperCase()

  if (names[existingCode]) {
    return {
      code: existingCode,
      name: names[existingCode],
      label: `${existingCode} - ${names[existingCode]}`,
      rank:
        AMBIENT_PRODUCTIVITY_ORDER.length - 1 - AMBIENT_PRODUCTIVITY_ORDER.indexOf(existingCode),
    }
  }

  const numericValues = [
    ...new Set(
      (Array.isArray(ambientValues) ? ambientValues : [])
        .map((item) => Number(item))
        .filter(Number.isFinite)
    ),
  ].sort((left, right) => left - right)
  const codes = AMBIENT_PRODUCTIVITY_CODES_BY_CLASS_COUNT[numericValues.length]
  const numericValue = Number(value)
  const rank = numericValues.findIndex((item) => item === numericValue)
  const code = codes?.[rank]

  if (!code) {
    return {
      code: rawValue || '-',
      name: '',
      label: rawValue || '-',
      rank: rank >= 0 ? rank : null,
    }
  }

  return {
    code,
    name: names[code],
    label: `${code} - ${names[code]}`,
    rank,
  }
}

export const buildDetailedAmbientComparisonRows = (
  cells = [],
  ambientValues = [],
  language = 'es'
) =>
  (Array.isArray(cells) ? cells : [])
    .map((cell) => ({
      ...cell,
      descriptor: getCopilotAmbientProductivity(cell?.ambientClass, ambientValues, language),
    }))
    .sort((left, right) => {
      const leftRank = finiteNumberOrNull(left.descriptor?.rank)
      const rightRank = finiteNumberOrNull(right.descriptor?.rank)
      if (leftRank !== null || rightRank !== null) {
        if (leftRank === null) return 1
        if (rightRank === null) return -1
        if (leftRank !== rightRank) return rightRank - leftRank
      }

      const leftProductivityIndex = finiteNumberOrNull(left?.soilProductivityIndex)
      const rightProductivityIndex = finiteNumberOrNull(right?.soilProductivityIndex)
      if (leftProductivityIndex !== null || rightProductivityIndex !== null) {
        if (leftProductivityIndex === null) return 1
        if (rightProductivityIndex === null) return -1
        if (leftProductivityIndex !== rightProductivityIndex) {
          return rightProductivityIndex - leftProductivityIndex
        }
      }

      return String(left?.soilUnit || '').localeCompare(String(right?.soilUnit || ''), language, {
        numeric: true,
        sensitivity: 'base',
      })
    })

const NUMERIC_AMBIENT_REFERENCE_PATTERN =
  /\b(?:Ambiente|Ambientes|Zone|Zones|Environment|Environments)\s+(-?\d+(?:\.0+)?)\b/gi
const AMBIENT_DIFFERENCE_PATTERN =
  /\b(Diferencia entre ambientes|Difference between zones|Difference between environments)\s+(-?\d+(?:\.0+)?)\s+(y|and)\s+(-?\d+(?:\.0+)?)\b/gi

const getHistoricalAmbientClassCount = (findings) => {
  const summary = findings.find((finding) => finding?.id === 'historical-ambient-summary')
  const classCountEvidence = summary?.evidence?.find((item) =>
    /^(Clases|Classes)$/i.test(String(item?.label || '').trim())
  )
  const classCount = Number(classCountEvidence?.value)
  return Number.isInteger(classCount) && classCount >= 2 && classCount <= 7 ? classCount : null
}

const getNumericAmbientClassIds = (findings) => {
  const classIds = new Set()

  findings.forEach((finding) => {
    ;(Array.isArray(finding?.evidence) ? finding.evidence : []).forEach((item) => {
      ;[item?.label, item?.value].forEach((value) => {
        const text = String(value || '')
        NUMERIC_AMBIENT_REFERENCE_PATTERN.lastIndex = 0
        let match = NUMERIC_AMBIENT_REFERENCE_PATTERN.exec(text)
        while (match) {
          const classId = Number(match[1])
          if (Number.isInteger(classId)) {
            classIds.add(classId)
          }
          match = NUMERIC_AMBIENT_REFERENCE_PATTERN.exec(text)
        }
      })
    })
  })

  return [...classIds].sort((left, right) => left - right)
}

const normalizeHistoricalAmbientEvidence = (findings, language) => {
  const classCount = getHistoricalAmbientClassCount(findings)
  const codes = AMBIENT_PRODUCTIVITY_CODES_BY_CLASS_COUNT[classCount]
  if (!codes) {
    return findings
  }

  const classIds = getNumericAmbientClassIds(findings)
  const usesOneBasedIds = !classIds.includes(0) && classIds.includes(classCount)
  const names =
    AMBIENT_PRODUCTIVITY_NAMES[
      String(language || '')
        .toLowerCase()
        .startsWith('en')
        ? 'en'
        : 'es'
    ]
  const formatClass = (value) => {
    const classId = Number(value)
    const rank = usesOneBasedIds ? classId - 1 : classId
    const code = codes[rank]
    return code ? `${code} - ${names[code]}` : String(value)
  }
  const formatText = (value) => {
    if (typeof value !== 'string') {
      return value
    }

    const withDifferences = value.replace(
      AMBIENT_DIFFERENCE_PATTERN,
      (_, prefix, highest, connector, lowest) =>
        `${prefix} ${formatClass(highest)} ${connector} ${formatClass(lowest)}`
    )

    const formatted = withDifferences.replace(
      NUMERIC_AMBIENT_REFERENCE_PATTERN,
      (match, classId) => {
        const prefix = match.slice(0, match.lastIndexOf(classId)).trimEnd()
        return `${prefix} ${formatClass(classId)}`
      }
    )

    const parts = formatted.split(/,\s*/)
    return parts.length > 1 &&
      parts.every((part) => getAmbientProductivityPriorityFromText(part) !== null)
      ? parts
          .sort(
            (left, right) =>
              getAmbientProductivityPriorityFromText(left) -
              getAmbientProductivityPriorityFromText(right)
          )
          .join(', ')
      : formatted
  }

  return findings.map((finding) => {
    const evidence = Array.isArray(finding?.evidence)
      ? finding.evidence.map((item) => ({
          ...item,
          label: formatText(item?.label),
          value: formatText(item?.value),
        }))
      : finding?.evidence

    return {
      ...finding,
      evidence:
        PRODUCTIVITY_ORDERED_FINDING_IDS.has(finding?.id) && Array.isArray(evidence)
          ? sortAmbientEvidence(evidence)
          : evidence,
    }
  })
}

const parseIsoDateParts = (value) => {
  const match = String(value || '').match(ISO_DATE_PATTERN)
  if (!match) {
    return null
  }

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null
  }

  return {year, month, day}
}

const formatIsoDateParts = ({year, month, day}) =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

const parseMonthDayNumber = (value) => {
  const match = String(value || '').match(MONTH_DAY_PATTERN)
  if (!match) {
    return null
  }

  const month = Number(match[1])
  const day = Number(match[2])
  const parsed = new Date(2000, month - 1, day)
  if (parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
    return null
  }

  return month * 100 + day
}

const isMonthDayInRange = (value, dateFrom, dateTo) => {
  const resolvedValue = parseMonthDayNumber(value)
  const resolvedFrom = parseMonthDayNumber(dateFrom)
  const resolvedTo = parseMonthDayNumber(dateTo)
  if (resolvedValue === null || resolvedFrom === null || resolvedTo === null) {
    return false
  }

  if (resolvedFrom <= resolvedTo) {
    return resolvedValue >= resolvedFrom && resolvedValue <= resolvedTo
  }

  return resolvedValue >= resolvedFrom || resolvedValue <= resolvedTo
}

export const buildVegetationIndexChartData = (ndviObservations = [], eviObservations = []) => {
  const pointsByDate = (observations) =>
    (Array.isArray(observations) ? observations : []).reduce((points, item) => {
      const date = typeof item?.date === 'string' ? item.date.trim() : ''
      const mean = finiteNumberOrNull(item?.mean)
      const dateParts = parseIsoDateParts(date)
      if (dateParts && mean !== null) {
        points.set(date, {
          x: Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day, 12),
          y: mean,
        })
      }
      return points
    }, new Map())

  const sortedPoints = (points) =>
    Array.from(points.values()).sort((first, second) => first.x - second.x)

  return {
    ndvi: sortedPoints(pointsByDate(ndviObservations)),
    evi: sortedPoints(pointsByDate(eviObservations)),
  }
}

export const getAgronomicSowingDateStatus = (sowingDate, calendar) => {
  if (!calendar || calendar.status !== 'available') {
    return 'unavailable'
  }
  if (!sowingDate) {
    return 'not_selected'
  }

  const parsedDate = parseIsoDateParts(sowingDate)
  if (!parsedDate) {
    return 'outside'
  }
  const monthDay = `${String(parsedDate.month).padStart(2, '0')}-${String(parsedDate.day).padStart(
    2,
    '0'
  )}`

  if (isMonthDayInRange(monthDay, calendar.usualDateFrom, calendar.usualDateTo)) {
    return 'usual'
  }
  if (isMonthDayInRange(monthDay, calendar.selectableDateFrom, calendar.selectableDateTo)) {
    return 'tolerance'
  }

  return 'outside'
}

export const getMaximumAgronomicAnalysisEndDate = (sowingDate, months = 7) => {
  const parsed = parseIsoDateParts(sowingDate)
  if (!parsed || !Number.isInteger(months) || months < 0) {
    return ''
  }

  const monthIndex = parsed.month - 1 + months
  const year = parsed.year + Math.floor(monthIndex / 12)
  const month = (monthIndex % 12) + 1
  const lastDay = new Date(year, month, 0).getDate()

  return formatIsoDateParts({
    year,
    month,
    day: Math.min(parsed.day, lastDay),
  })
}

export const hasValidAgronomicAnalysisPeriod = (value) => {
  const profile = normalizeAgronomicProfile(value)
  if (!profile.sowingDate || !parseIsoDateParts(profile.sowingDate)) {
    return false
  }
  if (!profile.harvestDate) {
    return true
  }
  if (!parseIsoDateParts(profile.harvestDate)) {
    return false
  }

  const maximumEndDate = getMaximumAgronomicAnalysisEndDate(profile.sowingDate)
  return profile.harvestDate >= profile.sowingDate && profile.harvestDate <= maximumEndDate
}

export const normalizeThermalTime = (value) => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const normalizePhotoperiod = (photoperiod) => {
    if (!photoperiod || typeof photoperiod !== 'object') {
      return null
    }

    return {
      enabled: typeof photoperiod.enabled === 'boolean' ? photoperiod.enabled : null,
      responseType: ['short_day', 'long_day'].includes(photoperiod.responseType)
        ? photoperiod.responseType
        : '',
      criticalHours: finiteNumberOrNull(photoperiod.criticalHours),
      sensitivityPerHour: finiteNumberOrNull(photoperiod.sensitivityPerHour),
      minimumFactor: finiteNumberOrNull(photoperiod.minimumFactor),
      appliesFromStageId:
        typeof photoperiod.appliesFromStageId === 'string'
          ? photoperiod.appliesFromStageId.trim()
          : '',
      appliesUntilStageId:
        typeof photoperiod.appliesUntilStageId === 'string'
          ? photoperiod.appliesUntilStageId.trim()
          : '',
    }
  }

  const normalizeVernalization = (vernalization) => {
    if (!vernalization || typeof vernalization !== 'object') {
      return null
    }

    return {
      enabled: typeof vernalization.enabled === 'boolean' ? vernalization.enabled : null,
      minimumTemperatureC: finiteNumberOrNull(vernalization.minimumTemperatureC),
      optimumTemperatureC: finiteNumberOrNull(vernalization.optimumTemperatureC),
      maximumTemperatureC: finiteNumberOrNull(vernalization.maximumTemperatureC),
      requiredUnits: finiteNumberOrNull(vernalization.requiredUnits),
      minimumFactor: finiteNumberOrNull(vernalization.minimumFactor),
      appliesFromStageId:
        typeof vernalization.appliesFromStageId === 'string'
          ? vernalization.appliesFromStageId.trim()
          : '',
      appliesUntilStageId:
        typeof vernalization.appliesUntilStageId === 'string'
          ? vernalization.appliesUntilStageId.trim()
          : '',
    }
  }

  return {
    source: value.source === 'custom' ? 'custom' : 'suggested',
    baseTemperatureC: finiteNumberOrNull(value.baseTemperatureC),
    optimumTemperatureC: finiteNumberOrNull(value.optimumTemperatureC),
    maximumTemperatureC: finiteNumberOrNull(value.maximumTemperatureC),
    stages: Array.isArray(value.stages)
      ? value.stages.map((stage) => ({
          stageId: typeof stage?.stageId === 'string' ? stage.stageId.trim() : '',
          thresholdGdd: finiteNumberOrNull(stage?.thresholdGdd),
        }))
      : [],
    photoperiod: normalizePhotoperiod(value.photoperiod),
    vernalization: normalizeVernalization(value.vernalization),
  }
}

export const hasValidThermalTime = (value, expectedStageIds = []) => {
  const thermalTime = normalizeThermalTime(value)

  if (
    !thermalTime ||
    thermalTime.baseTemperatureC === null ||
    thermalTime.baseTemperatureC < -10 ||
    thermalTime.baseTemperatureC > 30 ||
    thermalTime.optimumTemperatureC === null ||
    thermalTime.maximumTemperatureC === null ||
    thermalTime.optimumTemperatureC <= thermalTime.baseTemperatureC ||
    thermalTime.maximumTemperatureC <= thermalTime.optimumTemperatureC ||
    thermalTime.maximumTemperatureC > 50 ||
    thermalTime.stages.length === 0
  ) {
    return false
  }

  const stageIds = thermalTime.stages.map((stage) => stage.stageId)
  if (
    expectedStageIds.length > 0 &&
    (stageIds.length !== expectedStageIds.length ||
      stageIds.some((stageId, index) => stageId !== expectedStageIds[index]))
  ) {
    return false
  }

  const stagesValid = thermalTime.stages.every((stage, index) => {
    if (
      !stage.stageId ||
      stage.thresholdGdd === null ||
      stage.thresholdGdd <= 0 ||
      stage.thresholdGdd > 5000
    ) {
      return false
    }

    return index === 0 || stage.thresholdGdd > thermalTime.stages[index - 1].thresholdGdd
  })

  if (!stagesValid) {
    return false
  }

  const modifierStageRangeValid = (modifier) => {
    const fromIndex = stageIds.indexOf(modifier.appliesFromStageId)
    const untilIndex = stageIds.indexOf(modifier.appliesUntilStageId)
    return fromIndex >= 0 && untilIndex > fromIndex
  }

  if (thermalTime.photoperiod) {
    const photoperiod = thermalTime.photoperiod
    if (
      typeof photoperiod.enabled !== 'boolean' ||
      !['short_day', 'long_day'].includes(photoperiod.responseType) ||
      photoperiod.criticalHours === null ||
      photoperiod.criticalHours < 8 ||
      photoperiod.criticalHours > 18 ||
      photoperiod.sensitivityPerHour === null ||
      photoperiod.sensitivityPerHour < 0 ||
      photoperiod.sensitivityPerHour > 1 ||
      photoperiod.minimumFactor === null ||
      photoperiod.minimumFactor < 0.1 ||
      photoperiod.minimumFactor > 1 ||
      !modifierStageRangeValid(photoperiod)
    ) {
      return false
    }
  }

  if (thermalTime.vernalization) {
    const vernalization = thermalTime.vernalization
    if (
      typeof vernalization.enabled !== 'boolean' ||
      vernalization.minimumTemperatureC === null ||
      vernalization.optimumTemperatureC === null ||
      vernalization.maximumTemperatureC === null ||
      vernalization.minimumTemperatureC < -10 ||
      vernalization.minimumTemperatureC >= vernalization.optimumTemperatureC ||
      vernalization.optimumTemperatureC >= vernalization.maximumTemperatureC ||
      vernalization.maximumTemperatureC > 50 ||
      vernalization.requiredUnits === null ||
      vernalization.requiredUnits <= 0 ||
      vernalization.requiredUnits > 100 ||
      vernalization.minimumFactor === null ||
      vernalization.minimumFactor < 0.1 ||
      vernalization.minimumFactor > 1 ||
      !modifierStageRangeValid(vernalization)
    ) {
      return false
    }
  }

  return true
}

export const normalizeAgronomicProfile = (value) => {
  const profile = value && typeof value === 'object' ? value : {}
  const normalized = {
    crop: typeof profile.crop === 'string' ? profile.crop.trim() : '',
    genotypeId: typeof profile.genotypeId === 'string' ? profile.genotypeId.trim() : '',
    sowingDate: typeof profile.sowingDate === 'string' ? profile.sowingDate.trim() : '',
    harvestDate: typeof profile.harvestDate === 'string' ? profile.harvestDate.trim() : '',
    thermalTime: normalizeThermalTime(profile.thermalTime),
  }

  return normalized
}

export const hasCompleteAgronomicProfile = (value) => {
  const profile = normalizeAgronomicProfile(value)
  return Boolean(profile.crop && profile.genotypeId && profile.sowingDate)
}

export const getAgronomicProfileForQuestion = (analysis, fallbackProfile = null) => {
  const snapshotProfile = normalizeAgronomicProfile(analysis?.agronomicProfile)
  if (hasCompleteAgronomicProfile(snapshotProfile)) {
    return snapshotProfile
  }

  const normalizedFallback = normalizeAgronomicProfile(fallbackProfile)
  return hasCompleteAgronomicProfile(normalizedFallback) ? normalizedFallback : null
}

export const areAgronomicProfilesEqual = (left, right) => {
  const normalizedLeft = normalizeAgronomicProfile(left)
  const normalizedRight = normalizeAgronomicProfile(right)

  return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight)
}

export const hasAvailableAgronomicAnalysis = (value) =>
  Boolean(
    value &&
      typeof value === 'object' &&
      value.agronomicAnalysis?.status === 'available' &&
      hasCompleteAgronomicProfile(value.agronomicProfile)
  )

export const normalizeCopilotAnalysis = (value, language = 'es') => {
  const analysis = value && typeof value === 'object' ? value : {}
  const findings = Array.isArray(analysis.findings)
    ? normalizeHistoricalAmbientEvidence(
        normalizeEvidenceLabels(analysis.findings, language),
        language
      )
    : []

  const rawAiInterpretation =
    analysis.aiInterpretation && typeof analysis.aiInterpretation === 'object'
      ? analysis.aiInterpretation
      : null
  const aiInterpretationText =
    typeof rawAiInterpretation?.text === 'string' ? rawAiInterpretation.text.trim() : ''
  const aiInterpretationStatus =
    typeof rawAiInterpretation?.status === 'string' ? rawAiInterpretation.status : 'not_requested'
  const aiInterpretationIsComplete =
    aiInterpretationStatus !== 'available' || isCompleteCopilotAiText(aiInterpretationText)

  return {
    mode: analysis.mode === 'ai' ? 'ai' : 'grounded_rules',
    providerStatus: analysis.providerStatus || 'not_configured',
    answer: typeof analysis.answer === 'string' ? analysis.answer : '',
    aiInterpretation: aiInterpretationIsComplete
      ? {status: aiInterpretationStatus, text: aiInterpretationText}
      : {status: 'invalid_response', text: ''},
    findings,
    sources: Array.isArray(analysis.sources) ? analysis.sources : [],
    layerCatalog: Array.isArray(analysis.layerCatalog) ? analysis.layerCatalog : [],
    crossLayerAnalysis:
      analysis.crossLayerAnalysis && typeof analysis.crossLayerAnalysis === 'object'
        ? analysis.crossLayerAnalysis
        : {status: 'no_data', analyzedPairs: []},
    agronomicCatalog: Array.isArray(analysis.agronomicCatalog)
      ? analysis.agronomicCatalog.filter(
          (item) =>
            item &&
            typeof item.id === 'string' &&
            item.id.trim() &&
            typeof item.crop === 'string' &&
            item.crop.trim() &&
            typeof item.label === 'string' &&
            item.label.trim()
        )
      : [],
    agronomicProfile: normalizeAgronomicProfile(analysis.agronomicProfile),
    agronomicAnalysis:
      analysis.agronomicAnalysis && typeof analysis.agronomicAnalysis === 'object'
        ? analysis.agronomicAnalysis
        : {status: 'not_requested'},
    recentNdvi:
      analysis.recentNdvi && typeof analysis.recentNdvi === 'object'
        ? analysis.recentNdvi
        : {status: 'not_requested'},
    recentEvi:
      analysis.recentEvi && typeof analysis.recentEvi === 'object'
        ? analysis.recentEvi
        : {status: 'not_requested'},
    suggestedQuestions: Array.isArray(analysis.suggestedQuestions)
      ? analysis.suggestedQuestions.filter((item) => typeof item === 'string' && item.trim())
      : [],
    scope: analysis.scope && typeof analysis.scope === 'object' ? analysis.scope : {},
    notice: typeof analysis.notice === 'string' ? analysis.notice : '',
    analysisMetadata:
      analysis.analysisMetadata && typeof analysis.analysisMetadata === 'object'
        ? {
            analyzedAt:
              typeof analysis.analysisMetadata.analyzedAt === 'string'
                ? analysis.analysisMetadata.analyzedAt
                : '',
            analysisDate:
              typeof analysis.analysisMetadata.analysisDate === 'string'
                ? analysis.analysisMetadata.analysisDate
                : '',
            reused: analysis.analysisMetadata.reused === true,
            contractVersion: Number.isFinite(Number(analysis.analysisMetadata.contractVersion))
              ? Number(analysis.analysisMetadata.contractVersion)
              : 0,
            sourceFreshness: normalizeSourceFreshness(analysis.analysisMetadata.sourceFreshness),
            persistence: normalizeAnalysisPersistence(analysis.analysisMetadata.persistence),
          }
        : {
            analyzedAt: '',
            analysisDate: '',
            reused: false,
            contractVersion: 0,
            sourceFreshness: normalizeSourceFreshness(null),
            persistence: normalizeAnalysisPersistence(null),
          },
  }
}

const getLatestIntegratedPerformance = (integrated, language) => {
  const ambientRows = Array.isArray(integrated?.ambientRows) ? integrated.ambientRows : []
  const campaigns = Array.isArray(integrated?.campaigns) ? integrated.campaigns : []
  const fallbackCampaign = ambientRows
    .flatMap((row) => (Array.isArray(row?.campaigns) ? row.campaigns : []))
    .find((campaign) => campaign?.campaignKey !== null && campaign?.campaignKey !== undefined)
  const campaignKey = String(campaigns[0]?.campaignKey ?? fallbackCampaign?.campaignKey ?? '')

  if (!campaignKey || ambientRows.length < 2) {
    return null
  }

  const ambientValues = ambientRows.map((row) => row?.ambientClass)
  const rows = ambientRows
    .map((row) => {
      const performance = (Array.isArray(row?.campaigns) ? row.campaigns : []).find(
        (campaign) => String(campaign?.campaignKey ?? '') === campaignKey
      )
      const economicYieldTonsHa = finiteNumberOrNull(performance?.economicYieldTonsHa)
      const grossMarginUsdHa = finiteNumberOrNull(performance?.grossMarginUsdHa)

      if (economicYieldTonsHa === null && grossMarginUsdHa === null) {
        return null
      }

      return {
        ambient: getCopilotAmbientProductivity(row?.ambientClass, ambientValues, language).label,
        economicYieldTonsHa,
        grossMarginUsdHa,
      }
    })
    .filter(Boolean)
  const yieldRows = rows.filter((row) => row.economicYieldTonsHa !== null)
  const marginRows = rows.filter((row) => row.grossMarginUsdHa !== null)

  if (yieldRows.length < 2 || marginRows.length < 2) {
    return null
  }

  const highestYield = [...yieldRows].sort(
    (left, right) => right.economicYieldTonsHa - left.economicYieldTonsHa
  )[0]
  const highestMargin = [...marginRows].sort(
    (left, right) => right.grossMarginUsdHa - left.grossMarginUsdHa
  )[0]

  return {
    yieldAmbient: highestYield.ambient,
    marginAmbient: highestMargin.ambient,
    aligned: highestYield.ambient === highestMargin.ambient,
  }
}

export const buildCopilotResultInterpretation = (value, language = 'es') => {
  const analysis = value && typeof value === 'object' ? value : {}
  const findings = Array.isArray(analysis.findings) ? analysis.findings : []
  const findingById = new Map(findings.map((finding) => [finding?.id, finding]))
  const integrated = analysis.crossLayerAnalysis?.integratedAnalysis || {}
  const dimensions = integrated.availableDimensions || {}
  const items = []
  const hasFinding = (id) => findingById.has(id)
  const hasHistoricalAmbient =
    dimensions.historicalAmbient === true || hasFinding('historical-ambient-summary')
  const hasSoilMap =
    dimensions.soilMap === true ||
    hasFinding('soil-map-summary') ||
    hasFinding('soil-historical-ambient-cross')
  const hasProductiveResults =
    dimensions.yieldAndMargin === true ||
    hasFinding('historical-ambient-yield-cross') ||
    hasFinding('yield-history')

  if (hasFinding('same-soil-different-historical-behavior')) {
    items.push({id: 'structure', key: 'interpretation.structure_same_soil'})
  } else if (hasFinding('historical-ambient-soil-boundaries')) {
    items.push({id: 'structure', key: 'interpretation.structure_boundaries'})
  } else if (hasHistoricalAmbient && hasSoilMap && hasProductiveResults) {
    items.push({id: 'structure', key: 'interpretation.structure_complete'})
  } else if (hasHistoricalAmbient && hasSoilMap) {
    items.push({id: 'structure', key: 'interpretation.structure_partial'})
  }

  const classificationFinding = findingById.get('historical-ambient-classification-review')
  const classificationSummary =
    typeof classificationFinding?.summary === 'string' ? classificationFinding.summary.trim() : ''
  if (classificationSummary) {
    items.push({
      id: 'classification',
      text: classificationSummary,
    })
  }

  const performanceFinding = findingById.get('historical-ambient-yield-cross')
  const performanceEvidence = Array.isArray(performanceFinding?.evidence)
    ? performanceFinding.evidence
    : []
  const hasPerformanceSpread = performanceEvidence.length >= 3
  const performanceRelation = getLatestIntegratedPerformance(integrated, language)
  const performanceValues = hasPerformanceSpread
    ? {
        highestLabel: performanceEvidence[0]?.label || '-',
        highestValue: performanceEvidence[0]?.value || '-',
        lowestLabel: performanceEvidence[1]?.label || '-',
        lowestValue: performanceEvidence[1]?.value || '-',
        spread: performanceEvidence[2]?.value || '-',
        yieldAmbient: performanceRelation?.yieldAmbient || '-',
        marginAmbient: performanceRelation?.marginAmbient || '-',
      }
    : {
        yieldAmbient: performanceRelation?.yieldAmbient || '-',
        marginAmbient: performanceRelation?.marginAmbient || '-',
      }

  if (hasPerformanceSpread && performanceRelation?.aligned) {
    items.push({
      id: 'performance',
      key: 'interpretation.performance_spread_aligned',
      values: performanceValues,
    })
  } else if (hasPerformanceSpread && performanceRelation) {
    items.push({
      id: 'performance',
      key: 'interpretation.performance_spread_divergent',
      values: performanceValues,
    })
  } else if (hasPerformanceSpread) {
    items.push({
      id: 'performance',
      key: 'interpretation.performance_spread',
      values: performanceValues,
    })
  } else if (performanceRelation?.aligned) {
    items.push({
      id: 'performance',
      key: 'interpretation.performance_aligned',
      values: performanceValues,
    })
  } else if (performanceRelation) {
    items.push({
      id: 'performance',
      key: 'interpretation.performance_divergent',
      values: performanceValues,
    })
  }

  const economicFinding = findingById.get('economic-summary')
  const requiresEconomicReview = ['data-quality-warnings', 'yield-coverage'].some((id) => {
    const finding = findingById.get(id)
    return finding && ['warning', 'attention'].includes(finding.severity)
  })
  if (economicFinding?.severity === 'positive') {
    items.push({
      id: 'economics',
      key: requiresEconomicReview
        ? 'interpretation.economic_positive_with_review'
        : 'interpretation.economic_positive',
    })
  } else if (economicFinding?.severity === 'warning') {
    items.push({id: 'economics', key: 'interpretation.economic_negative'})
  } else if (economicFinding?.severity === 'attention') {
    items.push({id: 'economics', key: 'interpretation.economic_unverified'})
  }

  const recentNdviFinding = findingById.get('recent-ndvi')
  const hasNdvi = analysis.recentNdvi?.status === 'available'
  const hasEvi = analysis.recentEvi?.status === 'available'
  if (hasNdvi || hasEvi) {
    const english = String(language || '')
      .toLowerCase()
      .startsWith('en')
    const indices =
      hasNdvi && hasEvi ? (english ? 'NDVI and EVI' : 'NDVI y EVI') : hasNdvi ? 'NDVI' : 'EVI'
    const stageId = analysis.agronomicAnalysis?.phenology?.stageId
    const key =
      recentNdviFinding?.severity === 'warning' && stageId === 'maturity'
        ? 'interpretation.vegetation_maturity'
        : recentNdviFinding?.severity === 'warning'
        ? 'interpretation.vegetation_attention'
        : 'interpretation.vegetation_context'
    items.push({id: 'vegetation', key, values: {indices}})
  }

  return items
}

export const isEconomicReportFinding = (finding) => {
  if (!finding || typeof finding !== 'object') {
    return false
  }

  return Boolean(
    ECONOMIC_REPORT_FINDING_IDS.has(finding.id) ||
      finding.action?.type === 'open_report' ||
      (Array.isArray(finding.evidence) &&
        finding.evidence.some((item) => item?.source === 'economic_report'))
  )
}

export const groupCopilotFindings = (findings) => {
  const grouped = {
    economicReport: [],
    other: [],
  }

  if (!Array.isArray(findings)) {
    return grouped
  }

  findings.forEach((finding) => {
    grouped[isEconomicReportFinding(finding) ? 'economicReport' : 'other'].push(finding)
  })

  return grouped
}

export const getCopilotFindingTone = (severity) => {
  if (severity === 'warning') {
    return 'warning'
  }

  if (severity === 'attention') {
    return 'attention'
  }

  if (severity === 'positive') {
    return 'positive'
  }

  return 'info'
}

export const getCopilotActionLabelKey = (action) => {
  if (action?.type === 'open_report') {
    return 'actions.open_report'
  }

  if (action?.type === 'open_soil_map') {
    return 'actions.open_soil_map'
  }

  if (action?.type === 'open_yield_maps') {
    return 'actions.open_yield_maps'
  }

  if (action?.type === 'open_historical_ambient') {
    return 'actions.open_historical_ambient'
  }

  return null
}

export const getCopilotLayerImageType = (action) => {
  if (action?.type === 'open_soil_map') {
    return 'soil_map'
  }

  if (action?.type === 'open_yield_maps') {
    return 'yield_map'
  }

  return null
}
